import {
  type ActorView,
  getNotifications,
  markNotificationsSeen,
  type Notification,
} from "../../queries.ts";
import { authenticatedAppViewRequest, authState } from "../auth.ts";
import { AuthorizePrompt, SignInForm } from "../components/auth-panel.tsx";
import { PageHeader } from "../components/page-header.tsx";
import { PagedList } from "../components/paged-list.tsx";
import { clickable, NotificationSubject, PostCard } from "../components/post.tsx";
import { actorLabel, Avatar } from "../components/actor.tsx";
import { postPath, postPathFromUri, profilePath } from "../router.ts";
import { rkeyOf } from "../repo.ts";
import { cacheActor } from "../view-cache.ts";
import { RelativeTime } from "../components/post-time.tsx";
import { RpcClient } from "../api.ts";
import { isFiltered } from "../filters.ts";
import {
  canUseNotifications,
  notificationContext,
  notificationText,
  refreshUnread,
} from "../notifications.ts";

export function NotificationsView(signal: AbortSignal): Element {
  const page = <main class="notifications-page"></main>;
  let view = new AbortController();
  const subscription = authState.subscribeImmediate((state) => {
    view.abort();
    view = new AbortController();
    const viewSignal = AbortSignal.any([signal, view.signal]);
    if (state.status === "loading") {
      page.replaceChildren(<p class="status">Checking your account…</p>);
    } else if (state.status !== "authenticated") {
      page.replaceChildren(
        <section class="home-sign-in-box">
          <h2>Sign in to see your notifications</h2>
          {SignInForm(state.message, viewSignal)}
        </section>,
      );
    } else if (!canUseNotifications(state.scopes)) {
      page.replaceChildren(...AuthorizePrompt(
        state.did,
        "Authorize notification access to see who is talking to you.",
        "Authorize notifications",
        "/notifications",
        viewSignal,
      ));
    } else {
      page.replaceChildren(notificationList(state.did as `did:${string}`, viewSignal));
    }
  });
  signal.addEventListener("abort", () => {
    subscription.unsubscribe();
    view.abort();
  }, { once: true });
  return page;
}

function notificationList(viewer: `did:${string}`, signal: AbortSignal): Element {
  const client = new RpcClient(authenticatedAppViewRequest);
  let marked = false;
  const markSeen = (readThrough: string) => {
    void client.call(markNotificationsSeen, {
      parameters: {},
      input: { seenThrough: readThrough },
      signal,
    }).then(() => {
      if (!signal.aborted) void refreshUnread(signal);
    }).catch(() => {});
  };
  return (
    <section class="feed-section" aria-label="Notifications">
      {PageHeader({ title: "Notifications" })}
      {PagedList(
        (cursor) =>
          client.call(getNotifications, {
            parameters: { limit: 30, ...(cursor && { cursor }) },
            signal,
          }).then((page) => {
            if (!marked) {
              marked = true;
              markSeen(page.readThrough);
            }
            return { items: page.notifications, ...(page.cursor && { cursor: page.cursor }) };
          }),
        (item) => item.id,
        (item) => NotificationRow(item, viewer, signal),
        signal,
        "No notifications yet.",
      )}
    </section>
  );
}

const icons: Record<Notification["reason"], string> = {
  like: "like",
  repost: "repost",
  follow: "follow",
  reply: "reply",
  mention: "reply",
  quote: "reply",
};

function NotificationRow(
  item: Notification,
  viewer: `did:${string}`,
  signal: AbortSignal,
): Element {
  const subject = item.subject;
  const post = subject?.$type === "blue.cerulea.app.views#availablePost" ? subject.post : undefined;
  const [first, second] = item.authors;
  item.authors.forEach(cacheActor);
  // The notifier's own post renders as the row itself, Bluesky-style, with the
  // reason as its context line. Anything else is a row of who, what, and on which post.
  const context = post && post.author.did === first!.did
    ? notificationContext(item.reason)
    : undefined;
  if (post && context && !isFiltered(post)) {
    return (
      <article class={`notification notification-post${item.isRead ? "" : " notification-unread"}`}>
        {PostCard({ post, signal, variant: "notification", context })}
      </article>
    );
  }

  const subjectPath = post
    ? postPath(post.author, rkeyOf(post.uri))
    : subject?.$type === "blue.cerulea.app.views#unavailablePost"
    ? postPathFromUri(subject.uri)
    : undefined;
  const followers = `${profilePath(viewer)}/followers`;
  const othersPath = item.reason === "follow"
    ? followers
    : subjectPath && (item.reason === "like" || item.reason === "repost")
    ? `${subjectPath}/${item.reason}s`
    : undefined;
  const rowPath = item.reason === "follow"
    ? item.authorCount === 1 ? profilePath(first!.handle ?? first!.did) : followers
    : subjectPath;

  const profileLink = (actor: ActorView) => (
    <a
      class="notification-name"
      href={profilePath(actor.handle ?? actor.did)}
      dataset={{ route: "" }}
    >
      {actorLabel(actor)}
    </a>
  );
  const others = item.authorCount - 1;
  const count = `${others.toLocaleString()} ${others === 1 ? "other" : "others"}`;
  const rest = others === 1 && second
    ? profileLink(second)
    : othersPath
    ? <a href={othersPath} dataset={{ route: "" }}>{count}</a>
    : count;

  const row = (
    <article class={`notification notification-group${item.isRead ? "" : " notification-unread"}`}>
      <span
        class={`lucide notification-icon notification-${icons[item.reason]}`}
        aria-hidden="true"
      >
      </span>
      <div class="notification-body">
        <div class="notification-avatars">
          {item.authors.map((actor) => (
            <a
              href={profilePath(actor.handle ?? actor.did)}
              dataset={{ route: "" }}
              title={actorLabel(actor)}
            >
              {Avatar(actor)}
            </a>
          ))}
          {RelativeTime(new Date(item.indexedAt), signal)}
        </div>
        <p class="notification-line">
          {profileLink(first!)}
          {others ? " and " : undefined}
          {others ? rest : undefined}{" "}
          <span class="notification-text">{notificationText(item.reason)}</span>
        </p>
        {subject ? NotificationSubject(subject) : undefined}
      </div>
    </article>
  ) as HTMLElement;
  if (rowPath) clickable(row, rowPath, `${actorLabel(first!)} ${notificationText(item.reason)}`);
  return row;
}
